"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.percentOfAmount = exports.validatePrices = exports.validatePrice = exports.validateAmounts = exports.validateAmount = exports.toFixed = exports.splitPriceAmount = exports.sameQuantity = exports.samePrice = exports.roundQuantity = exports.roundPriceCents = exports.getRoundingSplit = exports.convertCentsToDollars = exports.calculateUnitTax = exports.calculateUnitPriceFromTotal = exports.calculateTotalQuantity = exports.calculateTotalPriceWithTax = exports.calculateTotalPriceByPrices = exports.calculateTotalPriceByPriceField = exports.calculateQuantityFromTotal = exports.calculatePriceWithTax = exports.calculatePriceFromTotal = exports.calculateItemUnitPrice = exports.calculateItemDue = exports.calculateDue = exports.biggerPrice = exports.biggerAmount = void 0;
const consts_1 = require("../types/consts");
const enums_1 = require("../types/enums");
/**
 * Checks if two prices are the same.
 *
 * @param firstPrice - The first price to compare.
 * @param secondPrice - The second price to compare.
 * @returns True if the prices are the same, false otherwise.
 */
function samePrice(firstPrice, secondPrice) {
    return Math.abs(toFixed(roundPriceCents(firstPrice) - roundPriceCents(secondPrice), 8)) < 0.01;
}
exports.samePrice = samePrice;
/**
 * Checks if the first price is greater than the second price.
 *
 * @param bigger - The bigger price.
 * @param smaller - The smaller price.
 * @returns True if the first price is greater than the second price, false otherwise.
 */
function biggerPrice(bigger, smaller) {
    return validatePrices([bigger, smaller]) && !samePrice(bigger, smaller) && bigger > smaller;
}
exports.biggerPrice = biggerPrice;
/**
 * Checks if the first amount is greater than the second amount.
 *
 * @param bigger - The bigger amount.
 * @param smaller - The smaller amount.
 * @returns True if the first amount is greater than the second amount, false otherwise.
 */
function biggerAmount(bigger, smaller) {
    return validateAmounts([bigger, smaller]) && !samePrice(bigger, smaller) && bigger > smaller;
}
exports.biggerAmount = biggerAmount;
/**
 * Validates that an amount is valid (not null or undefined).
 *
 * @param amount - The amount to validate.
 * @returns True if the amount is valid, false otherwise.
 */
function validateAmount(amount) {
    return !!amount || amount === 0;
}
exports.validateAmount = validateAmount;
/**
 * Validates that a price is valid (greater than or equal to zero).
 *
 * @param price - The price to validate.
 * @returns True if the price is valid, false otherwise.
 */
function validatePrice(price) {
    return price > 0 || price === 0;
}
exports.validatePrice = validatePrice;
/**
 * Validates an array of prices, checking if each price is valid (greater than or equal to zero).
 *
 * @param prices - An array of prices to validate.
 * @returns True if all prices are valid, false otherwise.
 */
function validatePrices(prices) {
    return prices?.every(validatePrice) ?? false;
}
exports.validatePrices = validatePrices;
/**
 * Validates an array of amounts, checking if each amount is valid (not null or undefined).
 *
 * @param amounts - An array of amounts to validate.
 * @returns True if all amounts are valid, false otherwise.
 */
function validateAmounts(amounts) {
    return amounts?.every(validateAmount) ?? false;
}
exports.validateAmounts = validateAmounts;
/**
 * Rounds a number to a specified precision and returns the result.
 *
 * @param num - The number to round.
 * @param precision - The number of decimal places to round to.
 * @returns The rounded number.
 */
function toFixed(num, precision) {
    return +(+(Math.round(+(num + 'e' + precision)) + 'e' + -precision)).toFixed(precision);
}
exports.toFixed = toFixed;
/**
 * Rounds the price to the nearest cent.
 *
 * @param price - The price to round.
 * @returns The rounded price.
 */
function roundPriceCents(price) {
    if (price < 0.001 && price > -0.001)
        return 0;
    return toFixed(price, consts_1.PRICE_DECIMAL_PLACES);
}
exports.roundPriceCents = roundPriceCents;
/**
 * Rounds the given quantity to the specified decimal places.
 *
 * @param amount The amount to round.
 * @returns The rounded amount.
 */
function roundQuantity(amount) {
    if (amount < 0.0001 && amount > -0.0001)
        return 0;
    return toFixed(amount, consts_1.QUANTITY_DECIMAL_PLACES);
}
exports.roundQuantity = roundQuantity;
/**
 * Checks if two quantities are approximately equal.
 * @param firstQuantity The first quantity.
 * @param secondQuantity The second quantity.
 * @returns True if the quantities are approximately equal, false otherwise.
 */
function sameQuantity(firstQuantity, secondQuantity) {
    return Math.abs(toFixed(firstQuantity - secondQuantity, 8)) < 0.0001;
}
exports.sameQuantity = sameQuantity;
/**
 * Calculates the price from the total price, taking into account the quantity and optional tax percentage.
 *
 * @param price - The price per unit.
 * @param taxPercent - The tax percentage (optional).
 * @param quantity - The quantity (default: DEFAULT_QUANTITY).
 * @returns The calculated price.
 */
function calculatePriceFromTotal(price, taxPercent, quantity = consts_1.DEFAULT_QUANTITY) {
    return roundPriceCents((quantity * price * 100) / (100 + (taxPercent ?? 0)));
}
exports.calculatePriceFromTotal = calculatePriceFromTotal;
/**
 * Calculates the price with tax.
 *
 * @param price - The price of the item.
 * @param taxPercent - The tax percentage to apply (optional).
 * @param quantity - The quantity of items (default: DEFAULT_QUANTITY).
 * @returns The calculated price with tax.
 */
function calculatePriceWithTax(price, taxPercent, quantity = consts_1.DEFAULT_QUANTITY) {
    const totalPrice = roundPriceCents(price * quantity);
    return roundPriceCents(((100 + (taxPercent ?? 0)) / 100) * totalPrice);
}
exports.calculatePriceWithTax = calculatePriceWithTax;
/**
 * Calculates the total price of an item including tax.
 *
 * @param price The price of the item.
 * @param isTaxInclusive Whether the price already includes tax.
 * @param taxPercent The tax percentage to apply, if `isTaxInclusive` is `false`.
 * @param quantity - The quantity of items (default: DEFAULT_QUANTITY).
 * @returns The total price, including tax.
 */
function calculateTotalPriceWithTax(price, isTaxInclusive, taxPercent, quantity = consts_1.DEFAULT_QUANTITY) {
    return isTaxInclusive ? roundPriceCents(price * quantity) : calculatePriceWithTax(price, taxPercent, quantity);
}
exports.calculateTotalPriceWithTax = calculateTotalPriceWithTax;
/**
 * Calculates the item unit price based on the given unit price, tax-inclusive status, and tax percentage.
 *
 * @param unitPrice - The unit price before tax.
 * @param isTaxInclusive - A flag indicating whether the price includes tax.
 * @param taxPercent - The tax percentage.
 * @returns The calculated unit price.
 */
function calculateItemUnitPrice(unitPrice, isTaxInclusive, taxPercent) {
    return isTaxInclusive ? calculatePriceFromTotal(unitPrice, taxPercent) : unitPrice;
}
exports.calculateItemUnitPrice = calculateItemUnitPrice;
/**
 * Calculates the unit price based on the given total price, tax-inclusive status, tax percentage, and quantity.
 *
 * @param totalPrice - The total price.
 * @param isTaxInclusive - A flag indicating whether the price includes tax.
 * @param taxPercent - The tax percentage.
 * @param quantity - The quantity of items (default: DEFAULT_QUANTITY).
 * @returns The calculated unit price.
 */
function calculateUnitPriceFromTotal(totalPrice, isTaxInclusive, taxPercent, quantity = consts_1.DEFAULT_QUANTITY) {
    const unitPrice = quantity ? roundPriceCents(totalPrice / quantity) : 0;
    return isTaxInclusive ? unitPrice : calculatePriceFromTotal(unitPrice, taxPercent);
}
exports.calculateUnitPriceFromTotal = calculateUnitPriceFromTotal;
/**
 * Calculates the quantity based on the total amount, unit price, and tax percentage.
 *
 * @param totalAmount - The total amount.
 * @param unitPrice - The unit price of the item.
 * @param isTaxInclusive - Indicates whether the tax is included in the unit price.
 * @param taxPercentage - The tax percentage (optional).
 * @returns The calculated quantity.
 */
function calculateQuantityFromTotal(totalAmount, unitPrice, isTaxInclusive, taxPercentage) {
    return unitPrice ? roundQuantity(totalAmount / calculateTotalPriceWithTax(unitPrice, isTaxInclusive, taxPercentage)) : 0;
}
exports.calculateQuantityFromTotal = calculateQuantityFromTotal;
/**
 * Calculates the unit tax based on the unit price, tax inclusiveness, and tax percentage.
 *
 * @param unitPrice The unit price.
 * @param isTaxInclusive Indicates whether the tax is included in the unit price.
 * @param taxPercent The tax percentage.
 * @returns The calculated unit tax.
 */
function calculateUnitTax(unitPrice, isTaxInclusive, taxPercent = 0) {
    return isTaxInclusive
        ? roundPriceCents(unitPrice - calculatePriceFromTotal(unitPrice, taxPercent))
        : roundPriceCents((unitPrice * taxPercent) / 100);
}
exports.calculateUnitTax = calculateUnitTax;
/**
 * Converts the given price in cents to dollars.
 *
 * @param price - The price in cents.
 * @returns The price in dollars.
 */
function convertCentsToDollars(price) {
    return roundPriceCents(price / 100);
}
exports.convertCentsToDollars = convertCentsToDollars;
/**
 * Calculates the rounding split between a requested price and an actual price.
 *
 * @param requestedPrice - The requested price.
 * @param price - The actual price.
 * @returns An object containing the number of splits and the split amount.
 */
function getRoundingSplit(requestedPrice, price) {
    const amountToSplit = roundPriceCents(requestedPrice - price);
    const splitsNumber = toFixed(Math.abs(amountToSplit) * 100, 0);
    const splitAmount = splitsNumber ? roundPriceCents(amountToSplit / splitsNumber) : 0;
    return { splitsNumber, splitAmount };
}
exports.getRoundingSplit = getRoundingSplit;
/**
 * Splits the given amount into multiple parts.
 *
 * @param amount - The amount to be split.
 * @param splitsNumber - The number of splits to be made (default: 1).
 * @returns An array of numbers representing the split amounts.
 */
function splitPriceAmount(amount, splitsNumber = 1) {
    const splitAmount = roundPriceCents(amount / splitsNumber);
    const remainder = roundPriceCents(amount - splitAmount * splitsNumber);
    const splits = new Array(splitsNumber).fill(splitAmount);
    if (remainder) {
        splits[0] = roundPriceCents(splits[0] + remainder);
    }
    return splits;
}
exports.splitPriceAmount = splitPriceAmount;
/**
 * Calculates the total price by a specified price field in an array of items.
 *
 * @template T - The type of the items in the array.
 * @param {T[]} items - The array of items.
 * @param {number} [startPrice=0] - The starting price.
 * @param {PriceFieldEnum} [priceField=PriceFieldEnum.PRICE] - The price field to use for calculation.
 * @returns {number} - The total price.
 */
function calculateTotalPriceByPriceField(items, startPrice = 0, priceField = enums_1.PriceFieldEnum.PRICE) {
    return (items ?? []).reduce((sum, item) => roundPriceCents(sum + (item?.[priceField] ?? 0)), startPrice);
}
exports.calculateTotalPriceByPriceField = calculateTotalPriceByPriceField;
/**
 * Calculates the total price by summing up the prices in the given array.
 *
 * @param prices - An array of prices.
 * @param startPrice - The starting price for the calculation. Defaults to 0.
 * @returns The total price.
 */
function calculateTotalPriceByPrices(prices, startPrice = 0) {
    return (prices ?? []).reduce((sum, price) => roundPriceCents(sum + (price ?? 0)), startPrice);
}
exports.calculateTotalPriceByPrices = calculateTotalPriceByPrices;
/**
 * Calculates the total quantity by summing up the given quantities.
 *
 * @param quantities - An array of quantities to be summed up.
 * @param startQuantity - The starting quantity for the calculation. Defaults to 0.
 * @returns The total quantity.
 */
function calculateTotalQuantity(quantities, startQuantity = 0) {
    return quantities.reduce((sum, quantity) => roundQuantity(sum + (quantity ?? consts_1.DEFAULT_QUANTITY)), startQuantity);
}
exports.calculateTotalQuantity = calculateTotalQuantity;
/**
 * Calculates the amount due based on the price and the amount already paid.
 *
 * @param price - The total price of the item.
 * @param paidAmount - The amount already paid. Defaults to 0.
 * @returns The amount due.
 */
function calculateDue(price, paidAmount = 0) {
    return roundPriceCents(price - paidAmount);
}
exports.calculateDue = calculateDue;
/**
 * Calculates the amount due for an item based on its price and the amount already paid.
 *
 * @template T - The type of the item, which must have a `price` and `paidAmount` property.
 * @param {T} item - The item for which to calculate the amount due.
 * @returns The amount due for the item.
 */
function calculateItemDue({ price, paidAmount }) {
    return calculateDue(price, paidAmount);
}
exports.calculateItemDue = calculateItemDue;
/**
 * Returns the percentage of an amount rounded to the hundreths (second) decimal place.
 *
 * Example: percentOfAmount(200, 80) returns 160
 *
 * @param amount - The amount.
 * @param percent - The percent of the amount that should be returned.
 * @returns The percentage of the amount.
 */
function percentOfAmount(amount, percent) {
    return roundPriceCents((percent / 100) * amount);
}
exports.percentOfAmount = percentOfAmount;
//# sourceMappingURL=price.util.js.map